// src/components/common/TokenDebugger.tsx

import React, { useState, useEffect } from 'react';
import {
  Card,
  Button,
  Space,
  Tag,
  Typography,
  Descriptions,
  message,
  Alert,
  Modal,
  Tooltip
} from 'antd';
import {
  ReloadOutlined,
  InfoCircleOutlined,
  ExclamationCircleOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined
} from '@ant-design/icons';
import { useAuth } from '../../hooks/useAuth';
import authService from '../../services/auth.service';
import apiService from '../../services/api';

const { Text } = Typography;

interface TokenInfo {
  token: string | null;
  isValid: boolean;
  payload: any;
  expiresAt: string | null;
  isExpired: boolean;
  timeToExpiry: number | null;
}

const TokenDebugger: React.FC = () => {
  const { user, isAuthenticated } = useAuth();
  const [accessTokenInfo, setAccessTokenInfo] = useState<TokenInfo | null>(null);
  const [refreshTokenInfo, setRefreshTokenInfo] = useState<TokenInfo | null>(null);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [sessionInfo, setSessionInfo] = useState<any>(null);
  const [isVisible, setIsVisible] = useState(false);

  // Parse JWT token
  const parseJwtToken = (token: string | null): TokenInfo => {
    if (!token) {
      return {
        token: null,
        isValid: false,
        payload: null,
        expiresAt: null,
        isExpired: true,
        timeToExpiry: null
      };
    }

    try {
      const parts = token.split('.');
      if (parts.length !== 3) {
        return {
          token,
          isValid: false,
          payload: null,
          expiresAt: null,
          isExpired: true,
          timeToExpiry: null
        };
      }

      const payload = JSON.parse(atob(parts[1]));
      const now = Math.floor(Date.now() / 1000);
      const isExpired = payload.exp ? payload.exp < now : true;
      const timeToExpiry = payload.exp ? payload.exp - now : null;
      const expiresAt = payload.exp ? new Date(payload.exp * 1000).toISOString() : null;

      return {
        token,
        isValid: true,
        payload,
        expiresAt,
        isExpired,
        timeToExpiry
      };
    } catch (error) {
      return {
        token,
        isValid: false,
        payload: null,
        expiresAt: null,
        isExpired: true,
        timeToExpiry: null
      };
    }
  };

  // Update token information
  const updateTokenInfo = () => {
    const accessToken = authService.getAccessToken();
    const refreshToken = authService.getRefreshToken();
    const session = authService.getSessionInfo();

    setAccessTokenInfo(parseJwtToken(accessToken));
    setRefreshTokenInfo(parseJwtToken(refreshToken));
    setSessionInfo(session);
  };

  // Manual token refresh
  const handleManualRefresh = async () => {
    setIsRefreshing(true);
    try {
      const success = await authService.refreshToken();
      if (success) {
        message.success('Token rafraîchi avec succès');
        updateTokenInfo();
      } else {
        message.error('Échec du rafraîchissement du token');
      }
    } catch (error: any) {
      message.error(`Erreur: ${error.message}`);
    } finally {
      setIsRefreshing(false);
    }
  };

  // Test API call to trigger automatic refresh
  const handleTestApiCall = async () => {
    try {
      message.info('Test d\'appel API en cours...');
      await apiService.get('/members?limit=1');
      message.success('Appel API réussi');
      updateTokenInfo();
    } catch (error: any) {
      message.error(`Échec de l\'appel API: ${error.message}`);
    }
  };

  // Format time remaining
  const formatTimeRemaining = (seconds: number | null): string => {
    if (!seconds) return 'N/A';
    if (seconds < 0) return 'Expiré';
    
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;
    
    if (hours > 0) {
      return `${hours}h ${minutes}m ${secs}s`;
    } else if (minutes > 0) {
      return `${minutes}m ${secs}s`;
    } else {
      return `${secs}s`;
    }
  };

  // Get token status color
  const getTokenStatusColor = (tokenInfo: TokenInfo | null): string => {
    if (!tokenInfo || !tokenInfo.isValid) return 'error';
    if (tokenInfo.isExpired) return 'error';
    if (tokenInfo.timeToExpiry && tokenInfo.timeToExpiry < 300) return 'warning'; // Less than 5 minutes
    return 'success';
  };

  // Get token status icon
  const getTokenStatusIcon = (tokenInfo: TokenInfo | null) => {
    if (!tokenInfo || !tokenInfo.isValid) return <ExclamationCircleOutlined />;
    if (tokenInfo.isExpired) return <ExclamationCircleOutlined />;
    if (tokenInfo.timeToExpiry && tokenInfo.timeToExpiry < 300) return <ClockCircleOutlined />;
    return <CheckCircleOutlined />;
  };

  useEffect(() => {
    updateTokenInfo();
    
    // Update every 10 seconds
    const interval = setInterval(updateTokenInfo, 10000);
    
    // Listen for token refresh events
    const handleTokenRefresh = () => {
      setTimeout(updateTokenInfo, 100);
    };
    
    window.addEventListener('auth:token-refreshed', handleTokenRefresh);
    
    return () => {
      clearInterval(interval);
      window.removeEventListener('auth:token-refreshed', handleTokenRefresh);
    };
  }, []);

  // Only show in development mode
  if (import.meta.env.PROD) {
    return null;
  }

  return (
    <>
      {/* Debug button */}
      <Tooltip title="Débogage des tokens (dev seulement)">
        <Button
          type="text"
          icon={<InfoCircleOutlined />}
          onClick={() => setIsVisible(true)}
          style={{
            position: 'fixed',
            bottom: 20,
            right: 20,
            zIndex: 1000,
            backgroundColor: 'rgba(0, 0, 0, 0.1)',
            border: '1px solid #d9d9d9'
          }}
        />
      </Tooltip>

      {/* Debug modal */}
      <Modal
        title="Débogage des Tokens"
        open={isVisible}
        onCancel={() => setIsVisible(false)}
        footer={null}
        width={800}
      >
        <Space direction="vertical" style={{ width: '100%' }} size="large">
          {/* Authentication Status */}
          <Alert
            message={`Statut d'authentification: ${isAuthenticated ? 'Connecté' : 'Non connecté'}`}
            type={isAuthenticated ? 'success' : 'error'}
            icon={isAuthenticated ? <CheckCircleOutlined /> : <ExclamationCircleOutlined />}
          />

          {/* User Info */}
          {user && (
            <Card size="small" title="Informations utilisateur">
              <Descriptions size="small" column={1}>
                <Descriptions.Item label="Email">{user.email}</Descriptions.Item>
                <Descriptions.Item label="Type">{user.userType}</Descriptions.Item>
                <Descriptions.Item label="Statut">{user.status}</Descriptions.Item>
              </Descriptions>
            </Card>
          )}

          {/* Access Token */}
          <Card 
            size="small" 
            title="Token d'accès"
            extra={
              <Tag 
                color={getTokenStatusColor(accessTokenInfo)} 
                icon={getTokenStatusIcon(accessTokenInfo)}
              >
                {accessTokenInfo?.isExpired ? 'Expiré' : 'Valide'}
              </Tag>
            }
          >
            {accessTokenInfo ? (
              <Descriptions size="small" column={1}>
                <Descriptions.Item label="Statut">
                  {accessTokenInfo.isValid ? 'Format valide' : 'Format invalide'}
                </Descriptions.Item>
                <Descriptions.Item label="Expire le">
                  {accessTokenInfo.expiresAt ? 
                    new Date(accessTokenInfo.expiresAt).toLocaleString() : 
                    'Non disponible'
                  }
                </Descriptions.Item>
                <Descriptions.Item label="Temps restant">
                  {formatTimeRemaining(accessTokenInfo.timeToExpiry)}
                </Descriptions.Item>
                {accessTokenInfo.payload && (
                  <Descriptions.Item label="ID Utilisateur">
                    {accessTokenInfo.payload.id}
                  </Descriptions.Item>
                )}
              </Descriptions>
            ) : (
              <Text type="secondary">Aucun token d'accès</Text>
            )}
          </Card>

          {/* Refresh Token */}
          <Card 
            size="small" 
            title="Token de rafraîchissement"
            extra={
              <Tag 
                color={getTokenStatusColor(refreshTokenInfo)} 
                icon={getTokenStatusIcon(refreshTokenInfo)}
              >
                {refreshTokenInfo?.isExpired ? 'Expiré' : 'Valide'}
              </Tag>
            }
          >
            {refreshTokenInfo ? (
              <Descriptions size="small" column={1}>
                <Descriptions.Item label="Statut">
                  {refreshTokenInfo.isValid ? 'Format valide' : 'Format invalide'}
                </Descriptions.Item>
                <Descriptions.Item label="Expire le">
                  {refreshTokenInfo.expiresAt ? 
                    new Date(refreshTokenInfo.expiresAt).toLocaleString() : 
                    'Non disponible'
                  }
                </Descriptions.Item>
                <Descriptions.Item label="Temps restant">
                  {formatTimeRemaining(refreshTokenInfo.timeToExpiry)}
                </Descriptions.Item>
              </Descriptions>
            ) : (
              <Text type="secondary">Aucun token de rafraîchissement</Text>
            )}
          </Card>

          {/* Session Info */}
          {sessionInfo && (
            <Card size="small" title="Informations de session">
              <Descriptions size="small" column={1}>
                <Descriptions.Item label="Heure de connexion">
                  {sessionInfo.loginTime ? 
                    new Date(sessionInfo.loginTime).toLocaleString() : 
                    'Non disponible'
                  }
                </Descriptions.Item>
                <Descriptions.Item label="Dernière activité">
                  {sessionInfo.lastActivity ? 
                    new Date(sessionInfo.lastActivity).toLocaleString() : 
                    'Non disponible'
                  }
                </Descriptions.Item>
                <Descriptions.Item label="Token présent">
                  {sessionInfo.hasToken ? 'Oui' : 'Non'}
                </Descriptions.Item>
                <Descriptions.Item label="Refresh token présent">
                  {sessionInfo.hasRefreshToken ? 'Oui' : 'Non'}
                </Descriptions.Item>
              </Descriptions>
            </Card>
          )}

          {/* Actions */}
          <Card size="small" title="Actions de test">
            <Space wrap>
              <Button 
                icon={<ReloadOutlined />}
                onClick={updateTokenInfo}
              >
                Actualiser les infos
              </Button>
              <Button 
                type="primary"
                loading={isRefreshing}
                onClick={handleManualRefresh}
                disabled={!refreshTokenInfo?.isValid || refreshTokenInfo?.isExpired}
              >
                Rafraîchir le token manuellement
              </Button>
              <Button 
                onClick={handleTestApiCall}
                disabled={!isAuthenticated}
              >
                Tester l'appel API
              </Button>
            </Space>
          </Card>

          {/* Warning */}
          <Alert
            message="Mode développement"
            description="Ce composant n'est visible qu'en mode développement et sera automatiquement masqué en production."
            type="warning"
            showIcon
          />
        </Space>
      </Modal>
    </>
  );
};

export default TokenDebugger;